import { useEffect, useState } from "react";

export default function LoginPage({ c, inp, dark, onToggleDark, supabase }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signupOpen, setSignupOpen] = useState(false);
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupPasswordRepeat, setSignupPasswordRepeat] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const isDark = c.bg === "#0f1117";

  useEffect(() => {
    try {
      const hash = String(window.location.hash || "").replace(/^#/, "");
      if (!hash) return;
      const params = new URLSearchParams(hash);
      const errorCode = params.get("error_code") || "";
      const errorDescription = params.get("error_description") || "";

      if (errorCode === "otp_expired") {
        setError("Der Bestätigungslink ist abgelaufen. Bitte erneut registrieren oder einen neuen Bestätigungslink anfordern.");
      } else if (errorDescription) {
        setError(errorDescription.replace(/\+/g, " "));
      }
    } catch {
      // Ignore malformed hash fragments.
    }
  }, []);

  const resetSignupForm = () => {
    setSignupName("");
    setSignupEmail("");
    setSignupPassword("");
    setSignupPasswordRepeat("");
  };

  const openSignup = () => {
    setError("");
    setInfo("");
    setSignupEmail(email);
    setSignupOpen(true);
  };

  const closeSignup = () => {
    if (creating) return;
    setSignupOpen(false);
    resetSignupForm();
  };

  const handleLogin = async () => {
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        setError(signInError.message || "Anmeldung fehlgeschlagen.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    setError("");
    setInfo("");

    if (!signupName.trim()) {
      setError("Bitte Ihren Namen eingeben.");
      return;
    }
    if (!signupEmail.trim() || !signupPassword) {
      setError("Bitte E-Mail und Passwort vollständig eingeben.");
      return;
    }
    if (signupPassword.length < 8) {
      setError("Das Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    if (signupPassword !== signupPasswordRepeat) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }

    setCreating(true);
    try {
      const { error: signUpError } = await supabase.auth.signUp({
        email: signupEmail,
        password: signupPassword,
        options: {
          data: {
            full_name: signupName.trim(),
            display_name: signupName.trim(),
          },
        },
      });
      if (signUpError) {
        setError(signUpError.message || "Konto konnte nicht erstellt werden.");
        return;
      }
      setEmail(signupEmail);
      setPassword("");
      setSignupOpen(false);
      resetSignupForm();
      setInfo("Anfrage gesendet ✓ Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse. Danach wartet Ihr Konto auf die Freigabe durch den Administrator.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: c.bg,
        color: c.text,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: c.bgSecondary,
          border: `1px solid ${c.border}`,
          borderRadius: 14,
          padding: 22,
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 22 }}>RadVentura</div>
            <div style={{ fontSize: 12, color: c.textSecondary }}>Geschützter Bereich</div>
          </div>
          <button
            onClick={onToggleDark}
            style={{
              background: dark ? "#2d3748" : "#e2e8f0",
              border: "none",
              borderRadius: 20,
              padding: "6px 12px",
              cursor: "pointer",
              fontSize: 16,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
            title="Theme wechseln"
            aria-label={isDark ? "Zu hellem Theme wechseln" : "Zu dunklem Theme wechseln"}
          >
            {dark ? "☀️" : "🌙"}
          </button>
        </div>

        <p style={{ margin: 0, marginBottom: 14, fontSize: 13, color: c.textSecondary }}>
          Bitte mit E-Mail und Passwort anmelden, um auf die Anwendung zuzugreifen.
        </p>

        <form
          onSubmit={(e) => { e.preventDefault(); handleLogin(); }}
          noValidate
        >
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <input
            type="email"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inp}
            placeholder="E-Mail"
            autoFocus
          />
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleLogin();
            }}
            style={inp}
            placeholder="Passwort"
          />
        </div>

        {error && (
          <div role="alert" aria-live="assertive" style={{ marginTop: 12, fontSize: 12, color: isDark ? "#fc8181" : "#cc3333" }}>
            {error}
          </div>
        )}
        {info && (
          <div role="status" aria-live="polite" style={{ marginTop: 12, fontSize: 12, color: isDark ? "#68d391" : "#276749" }}>
            {info}
          </div>
        )}

        <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="submit"
            disabled={loading || creating || !email.trim() || !password}
            style={{
              background: isDark ? "#0d2d1a" : "#f0fff4",
              color: isDark ? "#48bb78" : "#276749",
              border: `1px solid ${isDark ? "#48bb78" : "#9ae6b4"}`,
              borderRadius: "8px",
              cursor: loading || creating ? "not-allowed" : "pointer",
              padding: "8px 14px",
              fontSize: 13,
              fontWeight: 600,
              opacity: loading || creating ? 0.6 : 1,
            }}
          >
            {loading ? "Anmelden..." : "Anmelden"}
          </button>
          <button
            type="button"
            onClick={openSignup}
            disabled={loading || creating}
            style={{
              background: "transparent",
              color: c.textSecondary,
              border: `1px solid ${c.border}`,
              borderRadius: "8px",
              cursor: loading || creating ? "not-allowed" : "pointer",
              padding: "8px 14px",
              fontSize: 13,
              opacity: loading || creating ? 0.6 : 1,
            }}
            title="Konto für Erstzugang erstellen"
          >
            {creating ? "Erstelle Konto..." : "Konto erstellen"}
          </button>
        </div>
        </form>

      </div>

      {signupOpen && (
        <div
          onClick={closeSignup}
          style={{
            position: "fixed",
            inset: 0,
            background: dark ? "rgba(5, 8, 15, 0.72)" : "rgba(15, 23, 42, 0.36)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            zIndex: 1000,
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 460,
              background: c.bgSecondary,
              border: `1px solid ${c.border}`,
              borderRadius: 16,
              padding: 22,
              boxSizing: "border-box",
              boxShadow: dark ? "0 28px 60px rgba(0,0,0,0.4)" : "0 28px 60px rgba(15,23,42,0.16)",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 20 }}>Neues Konto erstellen</div>
                <div style={{ fontSize: 12, color: c.textSecondary, marginTop: 4 }}>
                  Bitte persönliche Daten eingeben. Danach erhalten Sie eine E-Mail zur Bestätigung Ihres Zugangs.
                </div>
              </div>
              <button
                onClick={closeSignup}
                disabled={creating}
                style={{
                  background: "transparent",
                  border: `1px solid ${c.border}`,
                  borderRadius: 999,
                  color: c.textSecondary,
                  width: 34,
                  height: 34,
                  cursor: creating ? "not-allowed" : "pointer",
                  fontSize: 16,
                  lineHeight: 1,
                }}
                title="Schließen"
              >
                ×
              </button>
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); handleCreateAccount(); }}
              noValidate
              style={{ display: "contents" }}
            >
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input
                type="text"
                name="name"
                autoComplete="name"
                value={signupName}
                onChange={(e) => setSignupName(e.target.value)}
                style={inp}
                placeholder="Vor- und Nachname"
                autoFocus
              />
              <input
                type="email"
                name="email"
                autoComplete="email"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                style={inp}
                placeholder="E-Mail"
              />
              <input
                type="password"
                name="new-password"
                autoComplete="new-password"
                value={signupPassword}
                onChange={(e) => setSignupPassword(e.target.value)}
                style={inp}
                placeholder="Passwort (min. 8 Zeichen)"
              />
              <input
                type="password"
                name="new-password-repeat"
                autoComplete="new-password"
                value={signupPasswordRepeat}
                onChange={(e) => setSignupPasswordRepeat(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreateAccount();
                }}
                style={inp}
                placeholder="Passwort wiederholen"
              />
            </div>

            <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={closeSignup}
                disabled={creating}
                style={{
                  background: "transparent",
                  color: c.textSecondary,
                  border: `1px solid ${c.border}`,
                  borderRadius: "8px",
                  cursor: creating ? "not-allowed" : "pointer",
                  padding: "8px 14px",
                  fontSize: 13,
                  opacity: creating ? 0.6 : 1,
                }}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={creating || !signupName.trim() || !signupEmail.trim() || !signupPassword || !signupPasswordRepeat}
                style={{
                  background: isDark ? "#0d2d1a" : "#f0fff4",
                  color: isDark ? "#48bb78" : "#276749",
                  border: `1px solid ${isDark ? "#48bb78" : "#9ae6b4"}`,
                  borderRadius: "8px",
                  cursor: creating ? "not-allowed" : "pointer",
                  padding: "8px 14px",
                  fontSize: 13,
                  fontWeight: 600,
                  opacity: creating ? 0.6 : 1,
                }}
              >
                {creating ? "Erstelle Konto..." : "Konto erstellen"}
              </button>
            </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
